import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  CpuChipIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { apiClient } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'
import IntelligenceMetrics from './IntelligenceMetrics'
import BehaviorSimulationStatus from './BehaviorSimulationStatus'
import SpamDetectionOverview from './SpamDetectionOverview'
import CrisisAlertsPanel from './CrisisAlertsPanel'

// Development mode flag
const isDevelopment = import.meta.env.DEV

interface IntelligenceDashboardProps {
  onSwitchToSettings?: () => void
  onSwitchToTemplates?: () => void
}

const IntelligenceDashboard: React.FC<IntelligenceDashboardProps> = ({
  onSwitchToSettings,
  onSwitchToTemplates
}) => {
  if (isDevelopment) console.log('🧠 IntelligenceDashboard: Component mounting/rendering')

  // Modal state management (only for alerts)
  const [showAllAlerts, setShowAllAlerts] = useState(false)

  const { data: intelligenceData, isLoading, error, status, fetchStatus } = useQuery({
    queryKey: ['intelligence-dashboard'],
    queryFn: async () => {
      if (isDevelopment) console.log('🚀 INTELLIGENCE DASHBOARD: Starting API call')
      if (isDevelopment) console.log('🔗 INTELLIGENCE DASHBOARD: URL will be /intelligence/dashboard')
      if (isDevelopment) console.log('⏰ INTELLIGENCE DASHBOARD: Timestamp:', new Date().toISOString())

      try {
        const result = await apiClient.get('/intelligence/dashboard')
        if (isDevelopment) console.log('✅ INTELLIGENCE DASHBOARD: API call successful')
        if (isDevelopment) console.log('📊 INTELLIGENCE DASHBOARD: Response data:', result)
        if (isDevelopment) console.log('🔍 INTELLIGENCE DASHBOARD: Response type:', typeof result)
        if (isDevelopment) console.log('🔍 INTELLIGENCE DASHBOARD: Response keys:', result ? Object.keys(result) : 'No keys')
        return result
      } catch (error) {
        console.error('❌ INTELLIGENCE DASHBOARD: API call failed in queryFn')
        console.error('📋 INTELLIGENCE DASHBOARD: Error object:', error)
        console.error('📋 INTELLIGENCE DASHBOARD: Error message:', error?.message)
        console.error('📋 INTELLIGENCE DASHBOARD: Error status:', error?.status)
        console.error('📋 INTELLIGENCE DASHBOARD: Error response:', error?.response)
        throw error
      }
    },
    refetchInterval: 30000,
    retry: (failureCount, error) => {
      if (isDevelopment) console.log(`🔄 INTELLIGENCE DASHBOARD: Retry attempt ${failureCount}`)
      if (isDevelopment) console.log('🔄 INTELLIGENCE DASHBOARD: Retry error:', error)
      return failureCount < 3
    },
    onSuccess: (data) => {
      if (isDevelopment) console.log('🎉 INTELLIGENCE DASHBOARD: onSuccess callback triggered')
      if (isDevelopment) console.log('📊 INTELLIGENCE DASHBOARD: Success data:', data)
    },
    onError: (error) => {
      console.error('💥 INTELLIGENCE DASHBOARD: onError callback triggered')
      console.error('💥 INTELLIGENCE DASHBOARD: Error in onError:', error)
      console.error('💥 INTELLIGENCE DASHBOARD: Error details:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        stack: error?.stack,
        status: error?.status,
        response: error?.response
      })
    }
  })

  // Log current state
  if (isDevelopment) console.log('📊 INTELLIGENCE DASHBOARD: Current query state:', {
    status,
    fetchStatus,
    isLoading,
    error: error?.message,
    hasData: !!intelligenceData,
    dataKeys: intelligenceData ? Object.keys(intelligenceData) : null
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    console.error('🚨 INTELLIGENCE DASHBOARD: Rendering error state')
    console.error('🚨 INTELLIGENCE DASHBOARD: Error object in render:', error)
    console.error('🚨 INTELLIGENCE DASHBOARD: Error message:', error?.message)
    console.error('🚨 INTELLIGENCE DASHBOARD: Error status:', error?.status)
    console.error('🚨 INTELLIGENCE DASHBOARD: Error stack:', error?.stack)

    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Failed to load intelligence data</p>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
          <p className="text-sm text-red-700 font-medium">Debug Information:</p>
          <p className="text-xs text-red-600 mt-1">
            Status: {status} | Fetch: {fetchStatus}
          </p>
          <p className="text-xs text-red-600">
            Error: {error?.message || 'Unknown error'}
          </p>
          <p className="text-xs text-red-600">
            Check browser console for detailed logs
          </p>
        </div>
      </div>
    )
  }

  const stats = intelligenceData?.data || {}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <CpuChipIcon className="w-8 h-8 text-indigo-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Intelligence Layer</h2>
          <p className="text-gray-600">AI-powered behavior simulation and content safety</p>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center space-x-3">
            <UserIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Behavior Profiles</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.behavior_profiles_count || 0}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center space-x-3">
            <ShieldCheckIcon className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Spam Detection Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.spam_detection_accuracy || '99.2'}%
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center space-x-3">
            <ClockIcon className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.avg_response_time || '45m'}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Active Alerts</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.active_crisis_alerts || 0}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Dashboard Components Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Behavior Simulation Status */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <BehaviorSimulationStatus
          data={stats.behavior_simulation}
          onConfigureSettings={onSwitchToSettings}
        />
        </motion.div>

        {/* Spam Detection Overview */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <SpamDetectionOverview
            data={stats.spam_detection}
            onConfigureRules={onSwitchToSettings}
          />
        </motion.div>
      </div>

      {/* Crisis Alerts Panel - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <CrisisAlertsPanel
          data={stats.crisis_alerts}
          onViewAllAlerts={() => setShowAllAlerts(true)}
          onResponseTemplates={onSwitchToTemplates}
        />
      </motion.div>

      {/* Intelligence Metrics - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <IntelligenceMetrics data={stats.intelligence_metrics} />
      </motion.div>

      {/* Modals */}

      {/* View All Alerts Modal */}
      {showAllAlerts && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">All Crisis Alerts</h3>
                  <button
                    onClick={() => setShowAllAlerts(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No active alerts</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      All systems are running smoothly. Crisis alerts will appear here when detected.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IntelligenceDashboard